import get from "lodash/get";
import find from "lodash/find";
import moment from "moment";

import { onlineT, OnlineItemGet, OnlineItemsFilterParams } from "../../typings/online";
import { EnumHelpers } from "../../utils/enum-helpers";
import { MAX_ONLINE_ITEMS } from "../../thor.const";
import { Enums, Events, MultiSelectType } from "../../thor.enums";
import { CategoryField } from "../../typings/categories";
import { paymentT } from "../../typings/payment";
import { findItemPaymentSlip, isItemPaymentGrouped } from "../../utils/payment";

export class OnlineContainerItemsController {
    public static $inject: string[] = [
        "$log",
        "$mdMedia",
        "$rootScope",
        "$scope",
        "$translate",
        "container",
        "onlineItemEdit",
        "localeNumber",
        "multipleSelect",
        "categoryService",
        "onlineDateService",
        "onlineItemService",
        "onlinePriceService",
        "paymentService",
        "utilsService"
    ];

    public isFiltersDisplayed: boolean;
    public filteredItemsCount: number;
    public filterQueryOptions = [
        { field: "meta.order_number.primary", label: "num_lot", searchIn: true },
        { field: "id", label: "id_lot_online", searchIn: true },
        { field: "description", label: "description", searchIn: true }
    ];
    public sortParams: any;
    public sortTypes: any;
    public context: string = "sort-items";
    public pastItemsResult: onlineT.past.ItemsResult;
    public itemCount: any = {};
    public saleStarted: boolean;
    public isContainerScheduled: boolean;
    public showBtnScroll: boolean = false;
    public backToTopTargetDOM: any;
    public containerGlobalStatus: any;
    public isMaxItemsReached: boolean;

    private filterParams: OnlineItemsFilterParams;
    private lastBidderItemsStatus = [
        onlineT.past.ItemStatus.PAID,
        onlineT.past.ItemStatus.UNPAID
    ];
    private $t;

    constructor(
        private $log: ng.ILogService,
        public $mdMedia: ng.material.IMedia,
        private $rootScope: ng.IRootScopeService,
        $scope: ng.IScope,
        $translate: ng.translate.ITranslateService,
        public container,
        private itemEdit,
        private localeNumber,
        multipleSelect,
        categoryService,
        private onlineDateService,
        private onlineItemService,
        private onlinePriceService,
        private paymentService,
        private utilsService
    ) {
        this.$t = $translate.instant;

        this.itemEdit.setCategories(
            categoryService.getCategoryField(container.resource.category_id)
        );
        this.itemEdit.setCommissionRates({
            voluntaryRate: get(container, "resource.options.commission_rate.voluntary")
        });
        this.isFiltersDisplayed = this.$mdMedia("gt-sm");

        if (this.container.items) {
            this.setUpGeneralInformations();
            this._setOnlineItems();
        }

        const checkAll = () => {
            this.container.checkIfEveryMandatoryFieldsAreFilled();
            this.container.checkIsPublishable();
            this.container.checkIsReadOnly();
            this.setUpGeneralInformations();
            this._filter();
            this._sort();
        };

        const resetDate = () => {
            if (
                !this.container.resource?.saleStarted &&
                [Enums.SaleStatus.Published, Enums.SaleStatus.Edited].includes(this.container.resource?.status)
            ) {
                this.container.resetDate();
            }
        };

        $rootScope.$on(Events.OnlineItemUpdated, (_, { fetchItems } = { fetchItems: false }) => {
            if (!fetchItems) {
                checkAll();
                resetDate();
                return;
            }

            this.container
                .fetchItems()
                .then(() => resetDate())
                .catch(error => this.$log.error(`Error while fetching items on sale ${this.container.id}`, error));
        });

        $rootScope.$on(Events.OnlineItemCreated, () => resetDate());

        $rootScope.$on(Events.OnlineItemsLoaded, () => checkAll());

        $rootScope.$on(Events.OnlineItemsPaymentsUpdated, () => this._setOnlineItems());

        $rootScope.$on(Events.MultiSelectAllOnline, () => {
            const { isPast, saleStarted, status } = this.container.resource || {};
            const isSalePublished = status === Enums.SaleStatus.Published;
            const elemType = isPast
                ? MultiSelectType.SalePast
                : isSalePublished && saleStarted
                ? MultiSelectType.SalePublished
                : MultiSelectType.SaleUpcoming;
            this.container.items?.forEach((item: OnlineItemGet) => {
                if (!item.filtered) {
                    multipleSelect.toggle(item, elemType, true);
                }
            });
            multipleSelect.evaluatePossibleActions();
        });

        this.initFilter();
        this.initSort();

        $scope.$watch(
            () => {
                return [this.filterParams, this.filterQueryOptions];
            },
            (current: any, previous: any) => {
                if (current[0] !== previous[0]) {
                    if (current[0].hidden !== previous[0].hidden || current[0].suppressed !== previous[0].suppressed) {
                        const { hidden, suppressed } = this.filterParams;
                        $rootScope.$emit(Events.OnlineItemsFilter, { hidden, suppressed });
                    }
                    if (current[0].field !== previous[0].field && current[0].category !== null) {
                        this.filterParams.category = null;
                        return;
                    }
                    this._filter();
                }
            },
            true
        );
    }

    public $onInit() {
        this.backToTopTargetDOM = document.getElementById("back-to-top");
    }

    public onSort(event: any = {}) {
        this.sortParams = angular.copy(event.sortParams || this.sortParams);
        this._sort();
    }

    public filterValidItems(item) {
        return !item.filtered;
    }

    public filterUngrouped(item) {
        return !item.grouped;
    }

    public generateFutureItemsHeader() {
        let header = "";
        if (this.itemCount.created > 0) {
            header = this.$t("x_lots_non_publies", [this.itemCount.created]);

            if (!this.container.resource?.start_at) {
                return header;
            }
            header += " / ";
        }
        if (this.container.resource?.saleStarted) {
            header += this.$t("x_lots_en_vente_sur_site_depuis_le_date", [
                this.itemCount.confirmed,
                moment(this.container.resource.start_at).format("dddd LL | LT"),
                Enums.Site.Interencheres
            ]);
            return header;
        }
        header += this.$t("x_lots_programmes", [this.itemCount.confirmed]);

        if (this.container.resource?.start_at) {
            header += ` ${this.$t("pour_une_mise_en_ligne_prevue_le_date", [
                moment(this.container.resource.start_at).format("dddd LL | LT")
            ])}`;
        }
        return header;
    }

    public generateListLabel(itemsCount: number) {
        let label;

        if (this.filterParams.hidden) {
            label = this.container.resource?.saleStarted
                ? "x_lots_retires_avant_la_cloture_des_encheres"
                : "x_lots_retires";
            return this.$t(label, [itemsCount]);
        }

        if (this.filterParams.suppressed) {
            label = this.container.resource?.saleStarted
                ? "x_lots_supprimes_avant_la_cloture_des_encheres"
                : "x_lots_supprimes";
            return this.$t(label, [itemsCount]);
        }
        return this.$t("x_lots_online", [itemsCount]);
    }

    public isItemEdited(item: OnlineItemGet) {
        return (
            item.status !== Enums.ItemStatus.Confirmed &&
            !item.states.hidden &&
            [Enums.SaleStatus.Published, Enums.SaleStatus.Edited].includes(this.container.resource?.status)
        );
    }

    protected getLastBidderItems({
        userId,
        status,
        item
    }: {
        userId: string;
        status: onlineT.past.ItemStatus;
        item: OnlineItemGet;
    }): OnlineItemGet[] {
        const slipPayment = findItemPaymentSlip(item);

        return this.pastItemsResult[status].items.filter(i => {
            // legacy case : 1 link-payment for 1 item
            if (slipPayment && slipPayment.items_ids.length === 1) {
                return i.id === item.id;
            }
            return userId && userId === get(i, "pricing.last_bid.user_id");
        });
    }

    protected getLastBidderPayments({
        userId,
        status
    }: {
        userId: string;
        status: onlineT.past.ItemStatus;
    }): paymentT.Payment[] {
        return this.pastItemsResult[status].items.reduce((acc: paymentT.Payment[], item: OnlineItemGet) => {
            if (!item.payment) {
                return acc;
            }
            item.payment.forEach((payment: paymentT.Payment) => {
                if (userId === payment.user_id && !find(acc, e => e.id === payment.id)) {
                    acc.push(payment);
                }
            });
            return acc;
        }, []);
    }

    private setUpGeneralInformations() {
        this.itemCount = {
            created: 0,
            confirmed: 0,
            suppressed: 0,
            hidden: 0
        };
        this.container.items.forEach(item => {
            if (item.states.hidden) {
                this.itemCount.hidden++;
            } else if (item.states.suppressed) {
                this.itemCount.suppressed++;
            } else {
                switch (item.status) {
                    case Enums.ItemStatus.Created:
                        this.itemCount.created++;
                        break;
                    case Enums.ItemStatus.Confirmed:
                        this.itemCount.confirmed++;
                        break;
                    default: break;
                }
            }
        });

        const { saleStarted, status, first_published } = this.container.resource || {};
        const { saleScheduled } = this.onlineDateService.getSaleStatus(this.container.resource);

        this.containerGlobalStatus = {
            status,
            saleStarted,
            isScheduled: saleScheduled,
            hasBeenPublishedOnce: !saleScheduled && !!first_published
        };

        this.isMaxItemsReached = this.container.getItemsPublishCount() >= MAX_ONLINE_ITEMS;
    }

    private initFilter() {
        this.filterParams = {
            id: null,
            query: "",
            media: null,
            description: null,
            field: null,
            category: null,
            hidden: false,
            suppressed: false,
            auctioned: null,
            isPast: this.container.resource.isPast,
            containerStatus: this.containerGlobalStatus,
            confirmed: false,
            caution: null,
            reserve_price: null,
            starting_price: 0,
            is_item_paid: false,
            is_item_unpaid: false,
            is_item_unsold: false
        };
    }

    private _filter() {
        let count = 0;
        this.container.items.forEach(item => {
            item.filtered = false;

            if (angular.isString(this.filterParams.media) && item.media_count) {
                item.filtered = true;
                return;
            }
            if (angular.isString(this.filterParams.description) && item.description) {
                item.filtered = true;
                return;
            }

            if (this.filterParams.field) {
                const fields = this.filterParams.field.split(",");
                if (fields.indexOf(item.category.field) === -1) {
                    item.filtered = true;
                    return;
                }
            }

            if (this.filterParams.category) {
                const categories = this.filterParams.category.split(",");
                if (categories.indexOf(item.category.id.toString()) === -1) {
                    item.filtered = true;
                    return;
                }
            }

            const query = this.utilsService.stringNormalize(this.filterParams.query);

            if (query !== "") {
                const desc = this.utilsService.stringNormalize(item.description);
                const itemId = get(item, "id", "").toString();
                const orderNumberPrimary = get(item, "meta.order_number.primary");
                const searchInDescription = find(this.filterQueryOptions, { field: "description" });
                const searchInID = find(this.filterQueryOptions, { field: "id" });
                const searchInOrderNumberPrimary = find(this.filterQueryOptions, {field: "meta.order_number.primary"});

                let shouldBefiltered = true;

                if (
                    (searchInDescription.searchIn && desc.includes(query)) ||
                    (searchInOrderNumberPrimary.searchIn && parseInt(query, 10) === orderNumberPrimary) ||
                    (searchInID.searchIn && itemId.includes(query))
                ) {
                    shouldBefiltered = false;
                }

                if (shouldBefiltered) {
                    item.filtered = true;
                    return;
                }
            }

            if (this.filterParams.hidden !== item.states.hidden) {
                item.filtered = true;
                return;
            }

            if (this.filterParams.suppressed !== item.states.suppressed) {
                item.filtered = true;
                return;
            }

            if (this.filterParams.starting_price && get(item, "pricing.starting_price", 0) !== 0) {
                item.filtered = true;
                return;
            }

            if (this.filterParams.confirmed && item.status === Enums.ItemStatus.Confirmed) {
                item.filtered = true;
                return;
            }

            if (this.filterParams.reserve_price && !get(item, "pricing.reserve_price", false)) {
                item.filtered = true;
                return;
            }

            const hasBidAmount = !!get(item, "pricing.last_bid.amount", false);

            if (this.filterParams.noBids && hasBidAmount) {
                item.filtered = true;
                return;
            }

            if (this.filterParams.withBids && !hasBidAmount) {
                item.filtered = true;
                return;
            }

            if (this.filterParams.isPast) {
                const isItemPaid: boolean = this.pastItemsResult[onlineT.past.ItemStatus.PAID].count > 0
                ? this.pastItemsResult[onlineT.past.ItemStatus.PAID].items.lastIndexOf(item) !== -1
                : false;
                if (this.filterParams.is_item_paid && !isItemPaid) {
                    item.filtered = true;
                    return;
                }
                const isItemUnpaid: boolean = this.pastItemsResult[onlineT.past.ItemStatus.UNPAID].count > 0
                ? this.pastItemsResult[onlineT.past.ItemStatus.UNPAID].items.lastIndexOf(item) !== -1
                : false;
                if (this.filterParams.is_item_unpaid && !isItemUnpaid) {
                    item.filtered = true;
                    return;
                }
                const isItemUnsold: boolean = this.pastItemsResult[onlineT.past.ItemStatus.UNSOLD].count > 0
                ? this.pastItemsResult[onlineT.past.ItemStatus.UNSOLD].items.lastIndexOf(item) !== -1
                : false;
                if (this.filterParams.is_item_unsold && !isItemUnsold) {
                    item.filtered = true;
                    return;
                }
            }
            item.filtered = false;
            count++;
        });
        this.filteredItemsCount = count;
        this._setOnlineItems();
    }

    private initSort() {
        this.sortParams = {
            orderBy: "orderNumber",
            reverse: false
        };

        this.sortTypes = [
            { value: "orderNumber", label: "num_lot" },
            { value: "id", label: "id_lot_online" },
            { value: "description", label: "description" },
            { value: "estimateMax", label: "estimation_filtre" },
            { value: "starting_price", label: "mise_a_prix" },
            { value: "reserve_price", label: "prix_de_reserve" },
            { value: "internalReference", label: "reference" }
        ];

        // after sale ended, sort on winner (default) and pricing are added to sortList
        if (this.container.resource.isPast) {
            this.sortTypes.splice(2, 0, { value: "pricing", label: "adjudication" });
            this.sortTypes.splice(0, 0, { value: "lastBidder", label: "adjudicataire" });
            this.sortParams.orderBy = "lastBidder";
        }

        this._sort();
    }

    private _sort() {
        const { reverse } = this.sortParams;
        switch (this.sortParams.orderBy) {
            case "lastBidder":
                this.container.items.sort((a, b) =>
                    this.utilsService.stringSort(
                        a,
                        b,
                        ["pricing.last_bid.last_name", "pricing.last_bid.first_name"],
                        reverse
                    )
                );
                break;
            case "id":
                this.container.items.sort((a, b) => this.utilsService.numericSort(a, b, "id", reverse));
                break;
            case "orderNumber":
                this.container.items.sort((a, b) => this.utilsService.orderNumberSort(
                    this.utilsService.getOrderNumber(a),
                    this.utilsService.getOrderNumber(b),
                    reverse
                ));
                break;
            case "starting_price":
            case "reserve_price":
                this.container.items.sort((a, b) => {
                    return this.utilsService.numericSort(a, b, `pricing.${this.sortParams.orderBy}`, reverse);
                });
                break;
            case "pricing":
                this.container.items.sort((a, b) => {
                    return this.utilsService.numericSort(a, b, "pricing.last_bid.amount", reverse);
                });
                break;
            case "estimateMax":
                this.container.items.sort((a, b) => {
                    return this.utilsService.numericSort(a, b, "pricing.estimates.max", reverse);
                });
                break;
            case "estimateMin":
                this.container.items.sort((a, b) => {
                    return this.utilsService.numericSort(a, b, "pricing.estimates.min", reverse);
                });
                break;
            case "internalReference":
                this.container.items.sort((a, b) => {
                    return this.utilsService.stringSort(a, b, "internal_reference", reverse);
                });
                break;
            case "description":
                this.container.items.sort((a, b) => {
                    return this.utilsService.stringSort(a, b, "description", reverse);
                });
                break;
            default:
                this.$log.error(`unable to sort by : ${this.sortParams.orderBy}`);
                break;
        }
        this._setOnlineItems();
    }

    private _setOnlineItems() {
        this.itemEdit.setItems(this.container.items);
        this._setSaleCurrency();

        if (this.container.resource && this.container.resource.isPast) {
            this._setPastItemsResult();

            if (get(this.sortParams, "orderBy") === "lastBidder") {
                this._setPastItemsGrouped();
                this._setPastItemsByLastBidder();
            } else {
                this._resetPastItemsByLastBidder();
            }
        }
    }

    private _setSaleCurrency() {
        this.itemEdit.saleCurrencySymbol = this.localeNumber.getCurrencySymbol(this.container.resource.currency);
    }

    private _setPastItemsResult() {
        this.pastItemsResult = this.container.items.reduce((acc: any, item: onlineT.Item) => {
            // get item sold price (fees included)
            const itemCategoryField: CategoryField = item.category?.field;
            const voluntaryRate = get(this.container, "resource.options.commission_rate.voluntary");
            const lastBidAmount = item.pricing?.last_bid?.amount || 0;
            const finalSoldPrice: number = this.onlinePriceService.calculateFinalPrice(
                lastBidAmount,
                { voluntaryRate },
                itemCategoryField,
                { format: false }
            );
            item.finalSoldPrice = finalSoldPrice;

            const isLastBidWinner = !!item.pricing?.last_bid?.is_winner;
            const isItemCanceled: boolean = get(item, "states.hidden", false);
            const isItemEnded: boolean = item.online?.end_at && new Date(item.online.end_at) < new Date();
            const isItemClosing: boolean = !isItemEnded || !item.pricing?.last_bid?.has_ended;
            const isItemUnsold: boolean = isItemEnded && !isLastBidWinner;
            const isItemPaid: boolean =
                !isItemCanceled && !isItemUnsold
                    ? this.paymentService.checkItemPaid(
                          item,
                          this.container.items,
                          this.container.salePaymentDeposit,
                          isItemPaymentGrouped(item, this.container.items)
                      )
                    : false;

            if (isItemCanceled) {
                return this._setItemResult(acc, item, onlineT.past.ItemStatus.CANCELED);
            }

            if (isItemUnsold) {
                return this._setItemResult(acc, item, onlineT.past.ItemStatus.UNSOLD);
            }

            if (isItemClosing) {
                return this._setItemResult(acc, item, onlineT.past.ItemStatus.CLOSING);
            }

            if (isItemPaid) {
                return this._setItemResult(acc, item, onlineT.past.ItemStatus.PAID);
            }

            return this._setItemResult(acc, item, onlineT.past.ItemStatus.UNPAID);
        }, this._resetPastItemsResult());

        this.$rootScope.$emit(Events.OnlineItemsSetted);
    }

    private _setPastItemsByLastBidder() {
        this.lastBidderItemsStatus.forEach(status => {
            this.pastItemsResult[status].items.map((item: onlineT.Item) => {
                const lastBidder = this.onlineItemService.getLastBidder(item);
                const userId = get(lastBidder, "user_id");
                const itemsByLastBidder = this.pastItemsResult[status].itemsByLastBidder;

                if (!userId) {
                    return;
                }

                if (!item.grouped) {
                    return;
                }

                const findLastBidder = find(itemsByLastBidder, el => el.lastBidder.user_id === userId);

                if (!findLastBidder) {
                    itemsByLastBidder.push({
                        lastBidder,
                        items: [item],
                        payments: this.getLastBidderPayments({ userId, status })
                    });
                } else {
                    findLastBidder.items.push(item);
                }

                return item;
            });
        });
    }

    private _resetPastItemsByLastBidder() {
        this.lastBidderItemsStatus.forEach(key => {
            this.pastItemsResult[key].itemsByLastBidder = [];
            this.pastItemsResult[key].items.map((item: OnlineItemGet) => (item.grouped = false));
        });
    }

    private _setPastItemsGrouped() {
        this.lastBidderItemsStatus.forEach(key =>
            this.pastItemsResult[key].items.forEach(
                (item: OnlineItemGet) => (item.grouped = isItemPaymentGrouped(item, this.pastItemsResult[key].items))
            )
        );
    }

    private _resetPastItemsResult(): onlineT.past.ItemsResult {
        const pastItemsResult = <onlineT.past.ItemsResult>{};
        const status = EnumHelpers.getValues(onlineT.past.ItemStatus);

        status.forEach((k: string) => pastItemsResult[k] = <onlineT.past.Items>{
            count: 0,
            items: [],
            itemsByLastBidder: []
        });
        return pastItemsResult;
    }

    private _setItemResult(acc: any, item: onlineT.Item, key: onlineT.past.ItemStatus): any {
        acc[key].items.push(item);
        if (!item.filtered) {
            acc[key].count++;
        }
        item.paymentStatus = key;
        return acc;
    }
}
